import { Fragment, useState, useEffect, useCallback } from 'react';
import intl from 'react-intl-universal';
import { Typography, Box } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { ThemeProvider } from 'styled-components';

import Lists from './components/Lists';
import Header from './components/Header';
import Filters from './components/Filters';
import Stack from '@mui/material/Stack';
import AlertWindow from './components/AlertPopupWindow';
import ConfirmPopUpWindow from './components/ConfirmPopUpWindow';
import { StyledConfirmModal } from 'shared/modals/Styles';

import { useMaterialUIController } from 'context';
import theme from 'assets/theme';
import themeDark from 'assets/theme-dark';

import useApi from 'shared/hooks/api';
import useMergeState from 'shared/hooks/mergeState';
import { PageLoader, PageError, RefreshButton } from 'components';
import localData from 'utils/localData';
import { updateArrayItemById } from 'utils/javascript';
import { modalWidths } from 'utils/styles';

// components and parts
import { USER_DATA } from 'shared/constants/users';
import { DOLPHIN_API } from 'shared/constants/apis';
import { isCurrentUserTaskManager } from 'utils/privilege';
import { NotAllowedPaper } from './styles';
import { Outlet } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import {
  IssueStatus,
  IssueState,
  IssueStatusToKey,
} from 'shared/constants/issues';
import api from 'utils/api';
import logger from 'utils/logger';
import { filterIssues, getSortedListIssues } from './components/Lists/utils';

const log = logger.createLogger('DashboardKanban');

const DashboardKanban = () => {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const { enqueueSnackbar } = useSnackbar();

  const isTaskManager = isCurrentUserTaskManager();

  const [alertModalOpen, setAlertModalOpen] = useState(false);
  const [alertModalInfo, setAlertModalInfo] = useState('');
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [confirmModalUpdateData, setConfirmModalUpdateData] = useState({
    destination: {
      droppableId: '',
      index: 0,
    },
    listPosition: 0,
    issueId: '',
    issueState: '',
    userIds: [],
    users: [],
  });
  const [confirmModalInfo, setConfirmModalInfo] = useState({
    source: '',
    destination: '',
    issueState: '',
  });

  const [filters, mergeFilters] = useMergeState({
    searchTerm: '',
    userIds: [],
    srcOrgNames: [],
    srcOrgIds: [],
    myOnly: false,
    recent: false,
  });
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [filteredIssues, setFilteredIssues] = useState([]);

  const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
  sevenDaysAgo.setHours(0, 0, 0, 0);
  let data, error, setLocalData, fetchProject;

  [{ data, error, setLocalData }, fetchProject] = useApi.useGet(
    DOLPHIN_API.PROJECT,
    {
      params: {
        organizationId: localData.get(USER_DATA.CURRENT_ORGANIZATION_ID),
        dateThres: sevenDaysAgo,
      },
    }
  );

  const updateIssue = () => {
    const { destination, listPosition, issueId, issueState, userIds, users } =
      confirmModalUpdateData;

    const updatedFields = {
      status: destination.droppableId,
      listPosition: listPosition,
      state: issueState,
    };
    if (userIds) {
      updatedFields.userIds = userIds;
    }
    if (users) {
      updatedFields.users = users;
    }

    api.optimisticUpdate(`${DOLPHIN_API.ISSUES}/${issueId}`, {
      updatedFields: updatedFields,
      currentFields: project.issues.find(({ id }) => id === issueId),
      setLocalData: (fields) => updateLocalProjectIssues(issueId, fields),
    });
  };

  const performFilterInIssues = useCallback(() => {
    if (!data) return;
    // Get all the issues from the project
    const allIssues = data.project.issues;
    // Filter based on IssueStatus, then sort by id and the filters the user has applied
    const filterByStatus = Object.values(IssueStatus).map((status) => {
      return getSortedListIssues(allIssues, status);
    });

    // Filter based on the filters the user has applied
    const filteredIssues = filterByStatus.map((status) => {
      return filterIssues(status, filters);
    });
    const mergedIssues = filteredIssues.flat();
    // Format the data
    const formattedIssues = mergedIssues.map((issue) => {
      return {
        id: issue.id,
        title: issue.title,
        patientName: issue.patientName,
        state: issue.state,
        status: issue.status,
        to_hospital: issue.dstOrgName,
        order_time: issue.createdAt,
      };
    });

    setFilteredIssues(formattedIssues);
  }, [data, filters]);

  useEffect(() => {
    performFilterInIssues();
  }, [performFilterInIssues]);

  if (!data) return <PageLoader />;
  if (error)
    return (
      <PageError
        errorCode={error.response.status}
        customErrorMessage={error.response.data.message}
        customErrorDescription={error.response.data.description}
      />
    );

  const { project } = data;

  const updateLocalProjectIssues = (issueId, updateFields) => {
    setLocalData((currentData) => ({
      project: {
        ...currentData.project,
        issues: updateArrayItemById(
          currentData.project.issues,
          issueId,
          updateFields
        ),
      },
    }));
  };

  const updateLocalProjectWithMoreDoneIssues = (updatedIssues) => {
    setLocalData((currentData) => ({
      project: {
        ...currentData.project,
        issues: updatedIssues,
      },
    }));
  };

  const handleRefreshClick = async () => {
    setIsRefreshing(true);
    await fetchProject();
    setIsRefreshing(false);
    enqueueSnackbar(intl.get('shared_data_was_reloaded'), {
      variant: 'success',
    });
  };

  const generateConfirmMessage = () => {
    if (confirmModalInfo.issueState === IssueState.NORMAL) {
      if (confirmModalInfo.destination === IssueStatus.CHECKREADY) {
        return intl.get('kanban_confirm_popup_window_text_check_ready');
      } else if (confirmModalInfo.destination === IssueStatus.DOCTORASSIGNED) {
        return intl.get('kanban_confirm_popup_window_text_doctor_assigned');
      } else if (confirmModalInfo.destination === IssueStatus.REPORTREADY) {
        return intl.get('kanban_confirm_popup_window_text_report_ready');
      } else if (confirmModalInfo.destination === IssueStatus.SENDBACK) {
        return intl.get('kanban_confirm_popup_window_text_send_back');
      }
    } else if (confirmModalInfo.issueState === IssueState.WARNING) {
      const fieldName = intl.get(
        IssueStatusToKey[confirmModalInfo.destination]
      );
      return (
        intl.get('kanban_confirm_popup_window_change_status_with_warning') +
        '\n' +
        '\n' +
        fieldName +
        '\n' +
        '\n' +
        intl.get(
          'kanban_confirm_popup_window_change_status_with_warning_detail'
        )
      );
    }
    return '';
  };

  // Get Priviliege and check if has
  return isTaskManager === true ? (
    <Fragment>
      <ThemeProvider theme={darkMode ? themeDark : theme}>
        <Box data-testid="kanban-box">
          <Stack direction="row" spacing={2}>
            <Header type="seimitsu" organizationName={project.name} />
            <RefreshButton
              darkMode={darkMode}
              tooltipTitle={intl.get('dashboard_refresh_button_tooltip_title')}
              handleRefreshClick={handleRefreshClick}
              isRefreshing={isRefreshing}
            />
          </Stack>

          <Grid container>
            <Grid size={12}>
              <Filters
                sourceOrganizations={project.contractsAsDstOrg}
                doctors={project.doctors}
                filters={filters}
                mergeFilters={mergeFilters}
              />
            </Grid>

            <Grid size={2} container justifyContent="flex-end"></Grid>
          </Grid>

          <Lists
            project={project}
            fetchProject={fetchProject}
            filters={filters}
            updateLocalProjectIssues={updateLocalProjectIssues}
            setAlertModalOpen={setAlertModalOpen}
            setAlertModalInfo={setAlertModalInfo}
            setConfirmModalInfo={setConfirmModalInfo}
            setConfirmModalOpen={setConfirmModalOpen}
            setConfirmModalUpdateData={setConfirmModalUpdateData}
            updateLocalProjectWithMoreDoneIssues={
              updateLocalProjectWithMoreDoneIssues
            }
          />

          <Outlet
            context={{
              doctors: project.doctors,
              updateLocalProjectIssues,
              issues: filteredIssues,
            }}
          />

          {alertModalOpen && (
            <StyledConfirmModal
              isOpen
              testid="modal:alert"
              width={modalWidths.xs}
              withCloseIcon={false}
              onClose={() => {
                setAlertModalOpen(false);
              }}
              renderContent={(modal) => (
                <AlertWindow
                  modalClose={modal.close}
                  alertModalInfo={alertModalInfo}
                />
              )}
            />
          )}

          {confirmModalOpen && (
            <StyledConfirmModal
              isOpen
              testid="modal:confirm"
              width={modalWidths.sm}
              withCloseIcon={false}
              onClose={() => {
                setConfirmModalOpen(false);
              }}
              renderContent={(modal) => (
                <ConfirmPopUpWindow
                  modalClose={modal.close}
                  updateIssue={updateIssue}
                  confirmMessage={generateConfirmMessage()}
                />
              )}
            />
          )}
        </Box>
      </ThemeProvider>
    </Fragment>
  ) : (
    <Fragment>
      <NotAllowedPaper elevation={10}>
        <Typography variant="h4" gutterBottom>
          {intl.get('kenshin_kanban_not_allowed_message')}
        </Typography>
        <br />
        <br />
      </NotAllowedPaper>
    </Fragment>
  );
};

export default DashboardKanban;
